import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Package, Clock, DollarSign } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getUserOrders } from '../../services/order.service';

const CustomDatePicker = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', 'calendar'],
    queryFn: () => getUserOrders({ type: 'purchases' })
  });

  const orders = useMemo(() => ordersData?.orders || [], [ordersData]);

  // Extract deliveries grouped by date
  const deliveriesByDate = useMemo(() => {
    const grouped = {};
    orders.forEach(order => {
      if (order.deliveryDate && (order.status === 'pending' || order.status === 'in-progress')) {
        const date = new Date(order.deliveryDate);
        const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(order);
      }
    });
    return grouped;
  }, [orders]);

  // Find the soonest delivery to select by default
  useEffect(() => {
    if (orders.length > 0 && selectedDay === null) {
      const pendingDeliveries = orders
        .filter(o => o.deliveryDate && (o.status === 'pending' || o.status === 'in-progress'))
        .sort((a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate));

      if (pendingDeliveries.length > 0) {
        const soonest = new Date(pendingDeliveries[0].deliveryDate);
        setCurrentDate(new Date(soonest.getFullYear(), soonest.getMonth(), 1));
        setSelectedDay(soonest.getDate());
      } else {
        const today = new Date();
        setSelectedDay(today.getDate());
      }
    }
  }, [orders, selectedDay]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getCalendarData = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const dates = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      dates.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(day);
    }
    return dates;
  };

  const navigateMonth = (direction) => {
    if (isAnimating) return;
    setIsAnimating(true);
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
    setSelectedDay(null);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleDateClick = (day) => {
    if (day) {
      setSelectedDay(day);
    }
  };

  const calendarDates = getCalendarData(currentDate);
  const monthYear = `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  // Get orders for the selected day
  const selectedDateKey = selectedDay ? `${currentDate.getFullYear()}-${currentDate.getMonth()}-${selectedDay}` : null;
  const ordersForSelectedDay = selectedDateKey ? (deliveriesByDate[selectedDateKey] || []) : [];

  return (
    <div className="w-full max-w-sm mx-auto bg-white dark:bg-[#1d2026] rounded-[10px] p-6 shadow-sm font-['Poppins'] border border-transparent dark:border-[#32353c]">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-6">
        <button
          onClick={() => navigateMonth('prev')}
          disabled={isAnimating}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        
        <h2 className="text-[15px] font-semibold text-[#141522] dark:text-white tracking-tight min-w-[140px] text-center">
          {monthYear}
        </h2>
        
        <button
          onClick={() => navigateMonth('next')}
          disabled={isAnimating}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50"
        >
          <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Grid */}
      <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-50' : 'opacity-100'}`}>
        <div className="grid grid-cols-7 gap-1 mb-3">
          {weekDays.map((day, index) => (
            <div key={index} className="h-10 flex items-center justify-center text-xs font-bold text-gray-400 dark:text-gray-500">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDates.map((date, index) => {
            const hasDelivery = date ? !!deliveriesByDate[`${currentDate.getFullYear()}-${currentDate.getMonth()}-${date}`] : false;
            const isSelected = date === selectedDay;

            return (
              <button
                key={index}
                onClick={() => handleDateClick(date)}
                disabled={!date || isAnimating}
                className={`
                  h-9 w-9 flex flex-col items-center justify-center text-[11px] font-medium rounded-full
                  transition-all duration-200 relative
                  ${!date 
                    ? 'cursor-default' 
                    : isSelected
                      ? 'bg-[#174568] dark:bg-blue-600 text-white scale-110'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }
                `}
              >
                {date && (
                  <>
                    <span>{date}</span>
                    {hasDelivery && !isSelected && (
                      <div className="absolute bottom-1.5 w-1 h-1 bg-[#174568] dark:bg-blue-400 rounded-full"></div>
                    )}
                    {hasDelivery && isSelected && (
                      <div className="absolute bottom-1.5 w-1 h-1 bg-white rounded-full"></div>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Order Details Dropup Area */}
      {selectedDay && (
        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-[#32353c] animate-in fade-in slide-in-from-bottom-2">
          <h3 className="text-[13px] font-medium text-[#141522] dark:text-gray-200 mb-2 px-1">
            {ordersForSelectedDay.length > 0 ? 'Deliveries for this day' : 'No deliveries scheduled'}
          </h3>
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {ordersForSelectedDay.map((order, idx) => (
              <div key={order._id || idx} className="flex items-center gap-1.5 p-1.5 bg-gray-50 dark:bg-gray-700/30 rounded-lg group hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors">
                <div className="w-8 h-8 rounded-md bg-[#17456815] dark:bg-blue-900/40 flex items-center justify-center text-[#174568] dark:text-blue-400">
                  <Package size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-gray-900 dark:text-white truncate">{order.title}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Clock size={7} /> {new Date(order.deliveryDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-semibold text-[#174568] dark:text-blue-400">${order.price}</p>
                </div>
              </div>
            ))}
            {ordersForSelectedDay.length === 0 && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 px-1 py-2 italic font-light">
                Click a date with a dot to see scheduled project deliveries.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="mt-4 flex items-center justify-between gap-1 px-1">
          <button
          onClick={() => {
            const today = new Date();
            setCurrentDate(today);
            setSelectedDay(today.getDate());
          }}
          className="text-[11px] font-medium text-[#174568] dark:text-blue-400 hover:underline"
        >
          Go to Today
        </button>
        <div className="flex items-center gap-2">
           <div className="flex items-center gap-1">
              <div className="w-1 h-1 bg-[#174568] dark:bg-blue-400 rounded-full"></div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">Delivery scheduled</span>
           </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDatePicker;